import { z } from 'zod';
/**
 * Schemas para sugestão de posts (Fase 8).
 *
 * Três modos de geração:
 *   - 'link'  — URL ou texto colado de um post de referência
 *   - 'topic' — pesquisa web sobre tópicos da área (requer Anthropic web search)
 *   - 'offer' — lançamento de oferta com lista de dores e CTA por comentário
 *     (template Yago Martins; pra anunciar template/checklist/curso/etc.)
 *
 * Drafts vivem em sessão por padrão. User salva explicitamente em
 * `post_drafts` (tabela) via endpoint /save.
 */
export const HookTypeSchema = z.enum([
    'curiosity_gap',
    'contrarian',
    'data',
    'narrative',
    'question',
    'build_in_public',
    'feature_launch',
]);
export const PostDraftSchema = z.object({
    id: z.string(),
    hookType: HookTypeSchema,
    hookLabel: z.string(),
    text: z.string(),
    charCount: z.number().int(),
    // Sweet spot LinkedIn 2026 é 3-5 hashtags; permitimos até 8 pra
    // tolerar pequenos excessos do LLM sem invalidar o draft inteiro.
    hashtags: z.array(z.string()).max(8),
    rationale: z.string().min(10),
    // Conteúdo complementar pra ser usado SÓ no carrossel (gerado junto pela
    // mesma chamada de IA, mas com ângulo/estrutura diferente do `text`). Quando
    // o user clica "Gerar carrossel", a heurística usa este campo em vez de
    // repetir a legenda. Opcional pra retrocompatibilidade — drafts antigos
    // que não têm vão usar `text` como fallback.
    carouselContent: z.string().nullable().optional(),
});
export const PostDraftSourceSchema = z.enum(['link', 'topic', 'offer']);
export const PostDraftSetSchema = z.object({
    schemaVersion: z.literal(1),
    sourceType: PostDraftSourceSchema,
    sourceInput: z.string(),
    /** Se Modo Link e fetch foi feito, o título extraído (apenas leitura). */
    sourceTitle: z.string().nullable().optional(),
    /** Quando Modo Link com fallback de texto colado. */
    sourceTextProvided: z.boolean().optional(),
    drafts: z.array(PostDraftSchema).min(1).max(5),
    generatedAt: z.string(),
    modelUsed: z.string(),
    /** Para Modo Tópico, sumário curto do que a pesquisa achou. */
    researchSummary: z.string().nullable().optional(),
    /** Citações de fontes encontradas (Modo Tópico). */
    sources: z
        .array(z.object({
        title: z.string(),
        url: z.string(),
    }))
        .default([]),
});
// Requests
import { LinkedInProfileSchema } from './pdf-snapshot.js';
export const PostsFromLinkRequestSchema = z
    .object({
    url: z.string().url().optional(),
    text: z.string().min(40).max(8000).optional(),
    note: z.string().max(500).optional(),
    // Profile cacheado no client. Se o byokStore do server perdeu o snapshot
    // (restart/TTL), client envia aqui pra evitar reupload do PDF.
    profile: LinkedInProfileSchema.optional(),
})
    .refine((v) => !!v.url || !!v.text, {
    message: 'Forneça URL ou texto do post.',
    path: ['url'],
});
/**
 * Modo "tendências": IA descobre tópicos quentes da área do user via web
 * search. User pode opcionalmente passar `audience` ou `hint` pra refinar
 * o escopo, mas NÃO precisa indicar tópico — a IA pesquisa.
 */
export const PostsFromTopicRequestSchema = z.object({
    audience: z.string().max(200).optional(),
    hint: z.string().max(500).optional(),
    // Mesma lógica do PostsFromLinkRequest: fallback de profile.
    profile: LinkedInProfileSchema.optional(),
});
/**
 * Carrossel programático (document/PDF post do LinkedIn).
 *
 * O LLM devolve JSON estruturado dos slides; o cliente renderiza HTML/CSS
 * com tipografia editorial (Newsreader serif + saffron accent + paper bg)
 * e exporta como PDF nativo via html2canvas + jsPDF. Algoritmo 2026 dá
 * 6.60% engagement médio pra carrossel vs 2-4% pra texto puro.
 */
export const CarouselSlideKindSchema = z.enum(['cover', 'content', 'closing']);
export const CarouselSlideSchema = z.object({
    index: z.number().int().min(1).max(10),
    kind: CarouselSlideKindSchema,
    // Título principal do slide. Cover usa título-hook curto; content usa
    // afirmação central da ideia; closing usa pergunta autêntica.
    title: z.string().min(3).max(180),
    // Corpo opcional abaixo do título (1-3 frases). Cover geralmente tem
    // só um subtítulo curto; closing geralmente tem só o título.
    body: z.string().max(320).nullable(),
    // Palavra(s) do título que devem aparecer com cor de acento + underline
    // saffron. Renderizada quando o cliente faz match com substring do título.
    // Use SÓ palavras EXATAS do título.
    accentText: z.string().max(40).nullable(),
    // Microtexto opcional no rodapé do slide (uppercase, sans).
    caption: z.string().max(140).nullable(),
});
export const CarouselSchema = z.object({
    schemaVersion: z.literal(1),
    // 5-10 slides totais. Cover é sempre o 1, closing sempre o último.
    slides: z.array(CarouselSlideSchema).min(5).max(10),
    generatedAt: z.string(),
    modelUsed: z.string(),
});
export const CarouselFromDraftRequestSchema = z.object({
    // Texto completo do draft (post de LinkedIn) que vai virar carrossel.
    draftText: z.string().min(80).max(3000),
    // Hashtags do draft (renderizadas opcionalmente no último slide).
    hashtags: z.array(z.string()).max(8).optional(),
    // Fallback: profile cacheado pra rehidratar voice se byokStore não tem.
    profile: LinkedInProfileSchema.optional(),
});
/**
 * Modo "lançamento de oferta" (template Yago Martins). User tem uma oferta
 * concreta (template, lista de prompts, checklist, curso, ferramenta) e quer
 * transformar em post viral seguindo a fórmula:
 *   [vocativo + promessa] → [protagonista testador] → [dimensão concreta]
 *   → [lista de dores → soluções] → [virada de exclusividade]
 *   → [resultado de negócio em 1 frase] → [CTA por comentário]
 */
export const OfferPainPointSchema = z.object({
    problem: z.string().min(3).max(200),
    solution: z.string().min(3).max(200),
});
export const PostsFromOfferRequestSchema = z.object({
    // Público-alvo no vocativo. Ex.: "Advogados", "Profissionais de Marketing".
    audienceVocative: z.string().min(2).max(80),
    // Nome da oferta. Ex.: "12 prompts pra vendas B2B".
    offerName: z.string().min(4).max(160),
    // O que está incluído na oferta (texto livre curto).
    offerDimension: z.string().min(10).max(600),
    // Pares dor → solução. Cada item vira uma linha com → no post.
    painPoints: z.array(OfferPainPointSchema).min(4).max(12),
    // Resultado de negócio em 1 frase ("Quem aplica X em Y consegue Z.").
    outcomeOneLiner: z.string().min(10).max(240),
    // Palavra-chave do CTA (UPPERCASE). Ex.: "PROMPTS", "TEMPLATE".
    commentKeyword: z
        .string()
        .min(2)
        .max(20)
        .regex(/^[A-Z0-9_-]+$/, 'Use só maiúsculas, números, _ ou -.'),
    // Quanto tempo testou (opcional). Ex.: "3 dias", "uma semana".
    testedTime: z.string().max(40).optional(),
    // Ferramenta usada (opcional). Ex.: "Claude", "ChatGPT".
    tool: z.string().max(60).optional(),
    // Fallback de profile (mesmo pattern dos outros modos).
    profile: LinkedInProfileSchema.optional(),
});
// Persistência opt-in
export const PostsSaveRequestSchema = z.object({
    draftSet: PostDraftSetSchema,
    /** Se user marcou um draft específico como "o escolhido" pra publicar. */
    selectedDraftId: z.string().nullable().optional(),
    label: z.string().max(120).optional(),
});
export const SavedPostDraftSchema = z.object({
    id: z.string().uuid(),
    createdAt: z.string(),
    sourceType: PostDraftSourceSchema,
    sourceInput: z.string(),
    draftSet: PostDraftSetSchema,
    selectedDraftId: z.string().nullable(),
    label: z.string().nullable(),
    archivedAt: z.string().nullable(),
});
//# sourceMappingURL=posts.js.map